import { useMemo } from "react"

import {
  useAgentMutationsAll,
  useAgentMutationsList,
  type AgentMutation,
} from "@/agents/agent-mutations"

import type { RunRowLike } from "@/types/RunRow"

interface UseRunResultsMutationCatalogParams {
  agentId?: string | null
  isShared: boolean
  runRows: RunRowLike[]
}

interface RunResultsMutationCatalog {
  mutationById: Map<string, Record<string, unknown>>
  mutationNameById: Map<string, string>
}

function buildMutationCatalog(
  agentMutations: AgentMutation[] | null | undefined,
  allMutations: AgentMutation[] | null | undefined,
  runRows: RunRowLike[],
): RunResultsMutationCatalog {
  const mutationNameById = new Map<string, string>()
  const mutationById = new Map<string, Record<string, unknown>>()

  for (const mutation of agentMutations ?? []) {
    mutationNameById.set(
      mutation.id,
      mutation.display_name?.trim() || mutation.id,
    )
    if (mutation.config_overrides) {
      mutationById.set(mutation.id, mutation.config_overrides)
    }
  }

  for (const mutation of allMutations ?? []) {
    if (!mutationNameById.has(mutation.id)) {
      mutationNameById.set(
        mutation.id,
        mutation.display_name?.trim() || mutation.id,
      )
    }
    if (mutation.config_overrides && !mutationById.has(mutation.id)) {
      mutationById.set(mutation.id, mutation.config_overrides)
    }
  }

  for (const row of runRows) {
    if (
      row.mutation_id &&
      row.mutation_name &&
      !mutationNameById.has(row.mutation_id)
    ) {
      mutationNameById.set(row.mutation_id, row.mutation_name.trim())
    }
  }

  return { mutationById, mutationNameById }
}

export function useRunResultsMutationCatalog({
  agentId,
  isShared,
  runRows,
}: UseRunResultsMutationCatalogParams): RunResultsMutationCatalog {
  const { data: allMutations } = useAgentMutationsAll({ enabled: !isShared })
  const { data: agentMutations } = useAgentMutationsList(agentId ?? "", {
    enabled: !isShared && Boolean(agentId),
  })

  return useMemo(
    () => buildMutationCatalog(agentMutations, allMutations, runRows),
    [agentMutations, allMutations, runRows],
  )
}
